import {NumberInput, TextInput} from 'platform/components';
import {Box, HStack, Show, Text, VStack} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';

import {isNotNil, not} from 'ramda';

import i18n from '@dms/i18n';
import {
  EitherQuantityOrError,
  MappedBasketItem,
  useBasketItemQuantity,
  useTenant,
} from '@dms/shared';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {CorrectionBasketItem} from '../types/CorrectionBasketItem';
import {quantitySchema} from '../utils/quantitySchema';

interface AfterSalesCorrectionBasketItemQuantityWithPriceProps<T extends CorrectionBasketItem>
  extends RequiredTestIdProps {
  item: MappedBasketItem<T>;
  handlingUnit: string | Nullish;
  onQuantityChange: (itemId: string, quantity: EitherQuantityOrError) => Promise<void>;
  hasPendingRequest: boolean;
}

export function AfterSalesCorrectionBasketItemQuantityWithPrice<T extends CorrectionBasketItem>(
  props: AfterSalesCorrectionBasketItemQuantityWithPriceProps<T>
) {
  const formatCurrency = useFormatCurrency();
  const tenant = useTenant();

  const {quantity, quantityError, handleQuantityChange} = useBasketItemQuantity({
    itemId: props.item.id,
    currentQuantity:
      props.item.correctionType === 'VALUE_CORRECTION'
        ? props.item.correctionValue
        : props.item.quantity,
    validationSchema: quantitySchema(
      props.item.minMaxQuantity?.minQuantity,
      props.item.minMaxQuantity?.maxQuantity,
      props.item.correctionType
    ),
    onQuantityChange: props.onQuantityChange,
  });

  const itemCurrency = props.item.totalPrice.withoutVat.currency ?? tenant.tenantCurrency;

  const totalPrice = formatCurrency(props.item.totalPrice.withoutVat.amount ?? 0, itemCurrency, 2);

  return (
    <HStack justify="space-between">
      <HStack spacing={2}>
        <Box width={29}>
          <NumberInput
            isDisabled={props.hasPendingRequest || not(props.item.quantityEditingAllowed)}
            isInvalid={isNotNil(quantityError)}
            isStepperVisible={props.item.correctionType === 'RETURN_TO_SUPPLIER'}
            value={quantity}
            errorMessage={quantityError}
            decimalPlaces={4}
            onChange={handleQuantityChange}
            data-testid={suffixTestId('quantity', props)}
          />
        </Box>
        <Box width={11}>
          <Show when={props.item.correctionType === 'RETURN_TO_SUPPLIER'}>
            <TextInput
              isDisabled
              value={props.handlingUnit ?? DEFAULT_HANDLING_UNIT}
              data-testid={suffixTestId('unit', props)}
            />
          </Show>
          <Show when={props.item.correctionType === 'VALUE_CORRECTION'}>
            <TextInput
              isDisabled
              value={itemCurrency}
              data-testid={suffixTestId('currency', props)}
            />
          </Show>
          <Show when={props.item.correctionType === 'RETURN_AND_PRICE_CHANGE'}>
            <TextInput
              isDisabled
              value={props.handlingUnit ?? DEFAULT_HANDLING_UNIT}
              data-testid={suffixTestId('unit', props)}
            />
          </Show>
        </Box>
      </HStack>

      <HStack>
        <VStack align="flex-end">
          <Text size="xSmall" color="tertiary">
            {i18n.t('general.labels.totalPrice')}
          </Text>
          <Text size="small" alternative data-testid={suffixTestId('price', props)}>
            {totalPrice}
          </Text>
        </VStack>
      </HStack>
    </HStack>
  );
}

const DEFAULT_HANDLING_UNIT = 'pcs';
